import { useCreateMutation } from '@/shared/api';
import { endpoints } from '@/shared/const/endpoints.const';
import { LS_CONCLUSION_FORM_DATA } from '@/shared/const/localstorage.const';
import {
  getRouteApplicationCheckDetails,
  getRouteUnderwriterConclusionForm,
} from '@/shared/const/router.const';
import { useToastNotif } from '@/shared/hooks';
import storage from '@/shared/lib/storage';
import { formatMoney, getAbsolutePath } from '@/shared/lib/utils';
import { PostRequestResponse } from '@/shared/types/requests.types';
import { PageCard, Stack } from '@/shared/ui';
import { Button } from '@/shared/ui/shadcn/ui/button';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import {
  createUnderwriterConclusionSchema,
  UnderwriterPledgeItem,
  UnderwriterPledgeItemKeys,
} from '../../model/schemas/underwriterConclusion.schema';

export default function UnderwriterConclusionPreview() {
  const { t } = useTranslation('applications');

  const schema = createUnderwriterConclusionSchema(t);
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToastNotif();

  const formData = storage.get(LS_CONCLUSION_FORM_DATA)
    ? (JSON.parse(storage.get(LS_CONCLUSION_FORM_DATA) ?? '') as z.infer<
        typeof schema
      > | null)
    : null;

  //Async tanstack mutation handler for creating conclusion
  const { mutate: createLawyerConclusion, isPending } = useCreateMutation<
    z.infer<typeof schema>,
    PostRequestResponse<Omit<z.infer<typeof schema>, 'product_object'>>
  >({
    url: endpoints.UNDERWRITER_CONCLUSION + '/' + params.id!,
    onSuccess(_data, _payload) {
      storage.remove(LS_CONCLUSION_FORM_DATA);
      showToast({
        message: t('Заключение Андрерайтера успешно создано'),
        type: 'success',
      });
      navigate(getAbsolutePath(getRouteApplicationCheckDetails(params.id!)));
    },
  });

  const backToForm = () =>
    navigate(`${getRouteUnderwriterConclusionForm(params.id!)}`);

  const formDataList = [
    {
      label: t('Основной вид деятельности'),
      value: formData?.company_main_direction ?? '',
    },
    {
      label: t('Дополнительный вид деятельности'),
      value: formData?.company_additional_direction ?? '',
    },
    {
      label: t('Тип финансирования'),
      value: formData?.financing_instrument ?? '',
    },
    {
      label: t('Цель'),
      value: formData?.self_pledge_product ?? '',
    },
    {
      label: t('Заключение'),
      value: formData?.underwriter_conclusion ?? '',
    },
  ];

  const pledgeList =
    formData?.pledge?.map((item) => {
      return (Object.keys(item) as UnderwriterPledgeItemKeys[]).map((key) => ({
        label: t(getLabelByKey(key)),
        value: getValueByKey(key, item),
      }));
    }) ?? [];

  const postHandler = () => {
    if (formData) {
      createLawyerConclusion(formData);
    }
  };

  const formDataListWithPledge = [...formDataList];

  return (
    <div className="outer-wrapper">
      <div className="scroll-container-wrapper relative h-[calc(100vh-227px)]">
        <div className="absolute inset-0 overflow-y-auto">
          <PageCard className="min-h-full pb-0" innerClassName="h-full">
            <Stack className="gap-y-4">
              <h1 className="text-20 font-semibold">
                {t('Заключение Андрерайтера')}
              </h1>

              <Stack className="divide-grey-e5e7eb w-[780px] divide-y">
                {formDataListWithPledge.map((item) => (
                  <div key={item.label} className="flex gap-x-[60px] py-4">
                    <div className="text-grey-6c737f w-[240px] text-sm font-medium">
                      {item.label}
                    </div>
                    <div className="font-base font-medium">{item.value}</div>
                  </div>
                ))}
              </Stack>
              {pledgeList.map((item, index) => (
                <Stack className="gap-y-4" key={index}>
                  <h3 className="text-18 font-semibold">
                    {t('Информация об обеспечении кредита') + ' ' + (index + 1)}
                  </h3>
                  <Stack className="divide-grey-e5e7eb w-[780px] divide-y">
                    {item.map((item) => (
                      <div key={item.label} className="flex gap-x-[60px] py-4">
                        <div className="text-grey-6c737f w-[240px] text-sm font-medium">
                          {item.label}
                        </div>
                        <div className="font-base font-medium">
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </PageCard>
        </div>
      </div>
      <div className="m1x-7 mt-auto pt-7">
        <Stack
          direction="horizontal"
          className="items-center justify-end gap-4 bg-white px-8 py-7"
        >
          <Button
            variant="secondary"
            type="button"
            size="lg"
            onClick={() => backToForm()}
          >
            {t('Редактировать')}
          </Button>
          <Button
            size="lg"
            type="submit"
            disabled={isPending}
            onClick={() => postHandler()}
          >
            {isPending && <Loader2 className="animate-spin" />}
            {t('Создать')}
          </Button>
        </Stack>
      </div>
    </div>
  );
}

function getLabelByKey(key: UnderwriterPledgeItemKeys) {
  switch (key) {
    case 'name':
      return 'Информация об обеспечении кредита';
    case 'external_appraiser_amount':
      return 'Рыночная стоимость';
    case 'available_amount_from_bank':
      return 'Залоговая стоимость';
    case 'percent':
      return 'Уровень покрытия';
    default:
      return key;
  }
}

function getValueByKey(
  key: UnderwriterPledgeItemKeys,
  item: UnderwriterPledgeItem,
) {
  switch (key) {
    case 'name':
      return item[key]?.toString() ?? '';
    case 'external_appraiser_amount':
      return `${formatMoney(item[key]?.toString() ?? '', true)} UZS`;
    case 'available_amount_from_bank':
      return `${formatMoney(item[key]?.toString() ?? '', true)} UZS`;
    case 'percent':
      return `${item[key]?.toString() ?? ''} %`;
    default:
      return item[key];
  }
}
